<script lang="ts">
  import * as echarts from "echarts/core";
  import { BarChart } from "echarts/charts";
  import { GridComponent, MarkLineComponent } from "echarts/components";
  import { CanvasRenderer } from "echarts/renderers";
  import type { EChartsType } from "echarts/core";
  import type { TrendViewModel } from "../lib/strokeTrend";
  import { strokesFrameLine } from "../lib/strokeTrend";

  interface Props {
    vm: TrendViewModel;
  }
  let { vm }: Props = $props();

  // Own-data frame of reference for the average number (their smoothest swim).
  // Null when there is no meaningful spread — then the card stays honestly simple.
  let frameLine = $derived(strokesFrameLine(vm));

  echarts.use([BarChart, GridComponent, MarkLineComponent, CanvasRenderer]);

  let chartEl: HTMLDivElement | null = $state(null);
  let chart: EChartsType | null = null;

  // Only mount/update the chart when the floor is met
  $effect(() => {
    if (!vm.coverage.meetsFloor) {
      // Dispose if we previously had a chart and data fell below floor
      chart?.dispose();
      chart = null;
      return;
    }
    if (!chartEl) return;

    if (!chart) {
      chart = echarts.init(chartEl, null, { renderer: "canvas" });
    }

    const data = vm.series.map((pt) => ({
      value: pt.strokesAvg, // bars = avg strokes per 25 (raw fact, G9)
      itemStyle: {
        color: "#A9CAEE",
        // Uniform (orch-8 ruling, reversible, 2026-09-11): the stroke cards'
        // brightness means "the last 5 swims the trend compares"; this chart
        // compares no windows, so a faded newest bar here meant the opposite.
        borderRadius: [4, 4, 0, 0],
      },
    }));

    const markLine = vm.overallStrokesAvg > 0
      ? {
          silent: true,
          symbol: "none",
          lineStyle: { color: "rgba(255,255,255,0.18)", type: "solid", width: 1 },
          label: { show: false },
          data: [{ yAxis: vm.overallStrokesAvg }],
        }
      : undefined;

    chart.setOption({
      grid: { left: 8, right: 8, top: 12, bottom: 12 },
      xAxis: {
        type: "category",
        data: vm.series.map(() => ""),
        axisTick: { show: false },
        axisLine: { lineStyle: { color: "rgba(255,255,255,0.06)" } },
        axisLabel: { show: false },
      },
      yAxis: {
        type: "value",
        show: false,
        splitLine: { show: false },
        // Do NOT invert the axis — lower bars = fewer strokes per length = smoother.
        // The direction cue below the chart makes that explicit (G9).
      },
      series: [
        {
          type: "bar",
          data,
          barWidth: "52%",
          barMinHeight: 2,
          ...(markLine ? { markLine } : {}),
        },
      ],
    });
  });

  $effect(() => {
    void vm.series;
    chart?.resize();
  });
</script>

<div class="stroke-trend-card">
  <!-- (1) A name and a count, like the stroke cards' tag row (orch-8 ruling,
       2026-09-11, glide option B: "rule 1 applies to the glide face too"). The
       count chip replaces "All N swims shown."; the sentence stays only while
       there is nothing to chart, where it is the whole message. -->
  <div class="glide-head">
    <!-- h2: the card is a section of the Strokes tab beside "Your strokes", not inside it. -->
    <h2 class="glide-title">Freestyle glide</h2>
    {#if vm.coverage.total > 0}
      <span class="swims-chip">
        {#if vm.coverage.indexed === vm.coverage.total}
          {vm.coverage.total} swim{vm.coverage.total === 1 ? "" : "s"}
        {:else}
          {vm.coverage.indexed} of {vm.coverage.total} swims
        {/if}
      </span>
    {/if}
  </div>
  {#if vm.coverage.indexed === 0}
    <p class="takeaway">{vm.takeawaySentence}</p>
  {/if}

  {#if vm.coverage.meetsFloor}
    <!-- (2) Per-swim chart -->
    <div class="chart-wrap" bind:this={chartEl}></div>

    <!-- (3) The two raw facts together, strokes big, seconds a stat chip beside
         the head's swim-count chip (VP round 3, 2026-09-11: the "Average across
         N swims" line repeated that chip and the seconds read as a sentence).
         No fused Glide number (G9). -->
    <div class="avg-block">
      <div class="big-stat-row">
        <span class="big-number">{vm.overallStrokesAvg}</span>
        <span class="big-unit">strokes per 25</span>
      </div>
      <span class="swims-chip stat-chip">{vm.overallSecondsAvg}s per 25</span>
    </div>
  {:else}
    <!-- Below-floor: still give ONE visual anchor (the tester's current starting
         point) so day one is not an all-text card (G12). -->
    {#if vm.coverage.indexed >= 1}
      <div class="starting-point">
        <span class="sp-dot" aria-hidden="true"></span>
        <span class="sp-label">Your starting point</span>
        <span class="sp-value">{vm.overallStrokesAvg} strokes</span>
      </div>
    {/if}
    <!-- Open-water-only history: the takeaway sentence above already names the
         pool rule — repeating it here read as saying the same thing three ways
         (persona gate 2026-07-11, B6 + polish). No prompt in that state. -->
    {#if !(vm.coverage.total === 0 && vm.coverage.owExcluded > 0)}
      <p class="below-floor-prompt">More freestyle swims will fill in the trend.</p>
    {/if}
  {/if}

  <!-- ONE tappable disclosure at the foot of the card, where every stroke card
       keeps its own (VP panel round 2, 2026-09-11: it sat above the chart). -->
  <!-- VP gate B3 (2026-07-09): the old copy taught a strokes+seconds SUM the
       card never displays (G9 deliberately killed the fused number). Teach the
       two raw facts the card actually shows instead. -->
  <!-- Open-water-only history: a per-LENGTH efficiency explainer under "Open
       water swims aren't counted here" speaks pool at the one swim just
       declared length-less (persona gate 2026-07-11, B6 — 4/5). The disclosure
       returns the moment a countable pool swim exists. -->
  {#if !(vm.coverage.total === 0 && vm.coverage.owExcluded > 0)}
    <details class="glide-info">
      <!-- "How it's counted", the stroke cards' words: the body IS the counting
           method (persona gate 2026-07-10). -->
      <summary>How it's counted</summary>
      <p class="glide-detail">
        For each length we look at two things: how many strokes you took, and how
        many seconds it took. When both go down, you're gliding more and fighting
        the water less. Watch the trend across swims, not any single number.
      </p>
      <!-- The smoothest-swim frame (brief item 3, 2026-09-11): a sentence, so it
           lives behind the tap. -->
      {#if vm.coverage.meetsFloor && frameLine}
        <p class="glide-detail">{frameLine}</p>
      {/if}
    </details>
  {/if}
</div>

<style>
  .stroke-trend-card {
    background: var(--bg-card);
    border: 0;
    border-radius: var(--radius-card);
    padding: var(--space-4, 16px);
    /* Explicit bottom padding ensures a visible bottom edge in ALL states,
       including below-floor where no chart occupies the space. */
    padding-bottom: var(--space-4, 16px);
    backdrop-filter: blur(22px) saturate(1.2);
    -webkit-backdrop-filter: blur(22px) saturate(1.2);
    display: flex;
    flex-direction: column;
    gap: var(--space-3, 12px);
  }

  /* Name left, count chip right: the stroke cards' tag row. */
  .glide-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--space-2, 8px);
  }
  .glide-title {
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary, #E4E2E4);
  }
  /* Same pill as the stroke cards' .tag. */
  .swims-chip {
    padding: 3px 10px;
    border-radius: var(--radius-pill);
    background: var(--bg-raised);
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  /* Empty-state sentence: 17px, primary text, plain. */
  .takeaway {
    font-size: 17px;
    font-weight: 500;
    color: var(--text-primary, #E4E2E4);
    margin: 0;
    line-height: 1.45;
  }

  /* One tappable disclosure. The summary is the only new interactive element;
     no modal, no tour. Kept at 15px with a clear affordance for low vision, and
     a 44 px target like the stroke cards' "How it's counted". */
  .glide-info {
    margin: 0;
  }
  .glide-info summary {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-accent, #B7C7E8);
    cursor: pointer;
    list-style: none;
    padding: 0;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    -webkit-tap-highlight-color: transparent;
  }
  .glide-info summary::-webkit-details-marker { display: none; }
  /* Caret cue so the line reads as expandable at arm's length. */
  .glide-info summary::after {
    content: "›";
    font-size: 18px;
    line-height: 1;
    transition: transform 0.15s ease;
  }
  .glide-info[open] summary::after { transform: rotate(90deg); }
  .glide-info summary:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
    border-radius: 4px;
  }
  .glide-detail {
    font-size: 15px;
    color: var(--text-body, #C5C6CE);
    margin: 4px 0 0;
    line-height: 1.45;
  }

  .chart-wrap {
    height: 120px;
    /* No top/bottom margin — gap handles spacing */
  }

  /* Average block: groups the two average figures under one label so neither
     is mistaken for the most-recent swim, and the seconds sit with the strokes
     they belong to (B4, persona gate 2026-07-10). */
  .avg-block {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .stat-chip {
    align-self: flex-start;
  }

  /* Big number row */
  .big-stat-row {
    display: flex;
    align-items: baseline;
    gap: var(--space-2);
  }

  .big-number {
    font-size: 42px;
    font-weight: 700;
    color: var(--text-primary, #E4E2E4);
    font-variant-numeric: tabular-nums;
    letter-spacing: -1.5px;
    line-height: 1;
  }

  .big-unit {
    font-size: 15px;
    color: var(--text-muted);
    letter-spacing: 0.04em;
  }

  /* Below-floor prompt: plain, 15px, no chart */
  .below-floor-prompt {
    font-size: 15px;
    color: var(--text-muted);
    margin: 0;
    line-height: 1.4;
  }

  /* Below-floor visual anchor: one dot + the current average so the card is
     never all text on day one. */
  .starting-point {
    display: flex;
    align-items: center;
    gap: var(--space-2, 8px);
    padding: var(--space-2, 8px) var(--space-3, 12px);
    background: rgba(255, 255, 255, 0.06);
    border-radius: var(--radius-button);
  }
  .sp-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #A9CAEE;
    flex: none;
  }
  .sp-label {
    flex: 1;
    font-size: 15px;
    color: var(--text-primary, #E4E2E4);
  }
  .sp-value {
    font-size: 20px;
    font-weight: 700;
    color: var(--text-primary, #E4E2E4);
    font-variant-numeric: tabular-nums;
  }
</style>
